

import React from 'react';
import PropTypes from 'prop-types';

import stringUtils from '../../../../../qti/utils/StringUtils';
import constants from '../../../../../qti/Constants';
import pciService from '../../../../../services/questionView/PCIService';
import KeyboardHelpAndPrompt from '../../../keyboardHelp/KeyboardHelpAndPrompt';
import { t } from '../../../../../services/i18nService';
import alertService from '../../../../../services/AlertService';
import deliveringService from '../../../../../services/DeliveringService';
import AlertLevel from '../../../../../components/alert/AlertLevel';
import sessionReader from '../../../../../store/readHelpers/SessionReader';
import navigationService from '../../../../../services/NavigationService';
import parentTestService from '../../../../../services/ParentTestService';
import marketplacePropertiesService from '../../../../../services/MarketplacePropertiesService';

/* eslint-disable no-unused-vars */
import styles from './pci.scss';
import settingsReader from '../../../../../store/readHelpers/SettingsReader';
import storeManager from '../../../../../store/StoreManager';
import assessmentActions from '../../../../../actions/AssessmentActions';

class PortableCustomInteraction extends React.Component {
    // These properties come from:
    // -QTIAssessmentItem.updateInteractions
    // -InteractionDisplay
    static propTypes = {
        callback: PropTypes.func.isRequired, // See ResponseService.handleUserResponse:339
        handleInteractionReady: PropTypes.func.isRequired,
        model: PropTypes.object.isRequired, // from parser
        isReadOnly: PropTypes.bool,
        extraResponses: PropTypes.array, // Contains RESPONSE_status from userResponse
        questionModel: PropTypes.object.isRequired, // server version of question
        userResponse: PropTypes.array,
        correctResponses: PropTypes.array,
        outcomeIsCorrect: PropTypes.string,
        services: PropTypes.object,
        settingValues: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.interactionUID = stringUtils.getUID();
    }

    componentDidMount() {
        window.addEventListener('message', this.receivePCIMessage, false);
        if (sessionReader.isViewMediaPdf() || sessionReader.isViewMediaPrint()) {
            this.props.handleInteractionReady(true);
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.sandbox) {
            if (pciService.shouldRerenderSandbox(Object.assign({}, this.props, {
                userResponse: this.props.userResponse,
            }), nextProps)) {
                this.sandbox = null;
                return true;
            }
            return false;
        }
        return true;
    }

    componentWillUnmount() {
        /* Seems that by the time we reach here, our iframe no longer has a contentWindow.
        Thus no apparent need to manually unload.
        this.sendMessageToIFrame({
            type: constants.pciMessage.DO_COMPLETED,
        });
        */
        window.removeEventListener('message', this.receivePCIMessage, false);
    }

    getTemplateVariables() {
        return this.props.model.templateVariables;
    }

    onPCILoadError() {
        alertService.showAlert(t('status.interactionLoadError'), {
            customFields: {
                level: AlertLevel.ERROR,
            },
        });
    }

    onPCIReady(data) {
        // We adjust our height based on the document height reported by the sandbox's inner document.
        if (!settingsReader.isPciBasedCustomAssessment()) {
            this.sandbox.style.height = `${data.height}px`;
        }
        this.props.handleInteractionReady(true);
    }

    /**
     * Checks if message comes from the same origin and that it is related to the interaction this component is
     * reponsible for.
     * @param event
     * @returns {boolean}
     */
    checkMessageComesFromMyPCI(event) {
        const { data, origin } = event;
        return location.origin === origin && data.interactionUID === this.interactionUID;
    }

    receivePCIMessage = (event) => {
        const { data } = event;

        // Is the message from this PCI? If yes, attend. If not, ignore.
        if (this.checkMessageComesFromMyPCI(event)) {
            window.console.log(`Received message: ${data.type}`, data);

            switch (data.type) {
            // The PCI notifies that the module lib has been loaded.
            case constants.pciMessage.MODULE_LOADED:
                this.sendMessageToInstantiateAndRenderPCI();
                break;

                // The PCI notifies that an instance of it has been created and it has been rendered.
            case constants.pciMessage.ON_READY:
                this.onPCIReady(data);

                break;

            case constants.pciMessage.MODULE_LOAD_ERROR:
                this.onPCILoadError();
                break;

                // The PCI is notifying that the user has explicitly terminated the attempt, often to request a hint.
            case constants.pciMessage.ON_DONE:
            case constants.pciMessage.RECEIVE_RESPONSE_AND_STATE:
                // The PCI is sending us its current response and state.
                this.onResponseAndStateReceived(data.response, data.state);

                break;
            default:
                // ignore
            }
        }
    }

    sendMessageToInstantiateAndRenderPCI() {
        let templateVariables = this.getTemplateVariables();
        if (settingsReader.isPciBasedCustomAssessment()) {
            templateVariables = this.addQueryParamsToTemplateVariables(templateVariables);
        }
        const boundTo = pciService.getBoundTo(this.props.correctResponses, this.props.model.boundTo);
        const status = pciService.getStatus(this.props);
        const state = pciService.getResumeState(this.props.model.extraResponseDeclarations, this.props.extraResponses);
        const isCorrectOutcome = pciService.isCorrectOutcome(this.props);
        const isPreviewMode = sessionReader?.isContextPreview();
        const properties = pciService.getProperties(
            this.props.model.properties,
            templateVariables,
            this.props.questionModel,
            isCorrectOutcome,
            isPreviewMode);

        this.sendMessageToIFrame({
            type: constants.pciMessage.GET_INSTANCE,
            typeIdentifier: this.props.model.typeIdentifier,
            configuration: {
                properties,
                templateVariables,
                boundTo,
                status,
            },
            state,
        });
    }

    addQueryParamsToTemplateVariables(templateVariables) {
        const assessmentRunId = sessionReader?.getAssessmentRunId();
        const userId = sessionReader?.getUserId();
        const externalId = sessionReader?.getExternalUserId();
        const partnerType = sessionReader?.getPartnerType();
        const globalReferenceId = sessionReader?.getGlobalReferenceId();
        const pciExternalAppAuthEndpoint = marketplacePropertiesService.isPciBasedCustomAssessmentEnabled() ?
            this.props?.settingValues?.['pci_external_app_auth_endpoint'] || '' : '';
        const pciExternalAppAuthApikey = marketplacePropertiesService.isPciBasedCustomAssessmentEnabled() ?
            this.props?.settingValues?.['pci_external_app_auth_apikey'] || '' : '';

        templateVariables.params = {
            assessmentRunId,
            userId,
            externalId,
            globalReferenceId,
            partnerType,
            pciExternalAppAuthEndpoint,
            pciExternalAppAuthApikey,
        };
        return templateVariables;
    }

    onResponseAndStateReceived(response, state) {
        const params = this.props.services.pciService.produceCallbackParameters(this.props.model, response, state);

        if (settingsReader.isPciBasedCustomAssessment() && state?.length) {
            const assessmentState = state[0];
            if (assessmentState === 'submitted') {
                deliveringService.deliverTest().then((result) => {
                    if (result?.pciDelivered === 'success') {
                        this.sendMessageToIFrame({
                            type: constants.pciMessage.DO_COMPLETED,
                        });
                    }
                });
            } else if (assessmentState === 'submissionAcknowledged') {
                const isCompositeTestContext = sessionReader.isParentTest();
                if (isCompositeTestContext) {
                    storeManager.dispatch(assessmentActions.setAssessmentState({}));
                    parentTestService
                        .loadParentTestChildTests()
                        .then(() => {
                            parentTestService.startParentServices()
                                .then(() => {
                                    navigationService.navigateToTestOverview();
                                });
                        });
                } else {
                    navigationService.navigateToDashboardTicket();
                }
            }
        }

        if (params) {
            this.props.callback(
                params.interactionIdentifier,
                params.userResponses || [],
                params.extraResponses
            );
        } else {
            window.console.log('We received a non-compliant response from the PCI interaction.', response);
        }
    }

    handleUserResponse = () => {
        this.sendMessageToIFrame({
            type: constants.pciMessage.REQUEST_RESPONSE_AND_STATE,
        });
    }

    onIFrameLoaded() {
        this.sendMessageToIFrame({
            type: constants.pciMessage.INIT,
            pciModuleConfiguration: this.props.services.pciService.getPCIConfiguration(this.props.questionModel),
            mainModule: this.props.model.mainModule,
            markup: this.props.model.markup,
        });
    }

    sendMessageToIFrame(message) {
        window.console.log(`Sending message to parent: ${message.type}`, message);

        this.props.services.pciService.postMessageToWindow(this.sandbox, this.interactionUID, message);
    }

    onEmptySandboxRendered(sandBox) {
        const cssPath = this.props.services.pciService.getPCIConfiguration(this.props.questionModel).cssPath;
        const content = this.props.services.pciService.buildSandboxHTML(
            this.interactionUID,
            cssPath,
            [constants.staticLibPaths.REQUIREJS, constants.staticLibPaths.PCI_COMMUNICATION_BRIDGE],
            this.props.questionModel.language
        );
        this.sandbox = sandBox;

        this.props.services.pciService.writeSandboxContent(
            sandBox,
            content,
            this.onIFrameLoaded.bind(this)
        );
    }

    render() {
        if (!this.interactionUID) {
            return null;
        }

        const correctness = this.props.outcomeIsCorrect === 'true' ? 'isCorrect' : 'isNotCorrect';
        const readOnlyStyle = `${styles.isReadOnly} ${styles[correctness]}`;
        const sandboxClassnames = `${styles.sandbox} ${(this.props.isReadOnly ? readOnlyStyle : '')}`;

        const keyboardHelpTextCaption = this.props.services.pciService.getKeyboardHelpTextCaptionIfAvailable(this.props.questionModel);
        // Important for resuming or showing the feedback page. This forces the iframe to rerender when the render() method
        // is used instead of reusing the already existing component. Note also that we only call render() when the condition
        // in shouldComponentUpdate is met.
        const sandboxKey = stringUtils.getUID();
        const showPrintPlaceholder = sessionReader.isViewMediaPdf() || sessionReader.isViewMediaPrint();
        const pciWrapperClass = settingsReader.isPciBasedCustomAssessment() ? 'pciWrapperMaximized' : 'sandbox';
        return !showPrintPlaceholder ? (
            <div className={pciWrapperClass}>
                <KeyboardHelpAndPrompt
                    captionKey={keyboardHelpTextCaption !== null ? keyboardHelpTextCaption : ''}
                    model={this.props.model}
                    showAccessibilityHelpers={!!keyboardHelpTextCaption}
                />

                <iframe
                    key={sandboxKey}
                    title={this.interactionUID}
                    ref={(c) => {
                        if (c) {
                            this.onEmptySandboxRendered(c);
                        }
                    }}
                    scrolling="no"
                    src="about:blank"
                    className={sandboxClassnames}
                />
            </div>
        ) : <div>{t('interactions.pci.pdfNotSupported')}</div>;
    }
}

export default PortableCustomInteraction;
